/**
 * FormSidebar
 *
 * Right-hand drawer for a form or record: entity, timeline, reminders, log and
 * fields views behind a segmented control. Consumers are `FormPageContent`,
 * `EntitiesPageContent` and `FormSidebarEntityContent` (nested record sidebar).
 *
 * `mask={false}` is what makes this a docked panel rather than a modal overlay,
 * and it is load-bearing. antd's default mask is an `inset: 0` element with
 * `pointer-events: auto` — the only part of the Drawer that covers the page,
 * since `.ant-drawer` itself is `pointer-events: none` — so clicks meant for
 * main-pane fields landed on it, and the focus trap it turns on bounced focus
 * back into the panel. Dropping it also drops the body scroll lock. Esc still
 * closes, keyed off `keyboard`; what goes is click-outside-to-close, which no
 * view depended on — each exits by the header close button or its parent.
 *
 * Controlled `open={isOpen}` + `destroyOnHidden`: the Drawer stays mounted so the
 * leave animation and focus restore run, while hidden view content unmounts and
 * unsubscribes. That also discards rc-drawer's own resize state, so the width is
 * owned by `uiState` — a store that outlives this component — which clamps it
 * and persists it per user, and drawn with `drawerDraggerStyle` so the drag
 * handle is visible. `drawerWrapperStyle` opens it full width below antd `md`,
 * where the Segmented is small and sizes each tab to its label instead of to
 * an equal share, so no tab label truncates on a phone. Closing is
 * asymmetric — the Drawer's close routes through `SidebarControlContext` in the
 * entity view, `onClose` otherwise; either way the just-created-record mark is
 * dropped, so a reopened record mounts locked.
 */

import { Drawer, Grid, Segmented, Skeleton, theme } from 'antd';
import { lazy, Suspense, useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { SidebarView } from '../../../providers/SidebarControlProvider';
import { SidebarControlContext } from '../../../providers/SidebarControlContext';
import { panelMaxWidth, setPanelWidth, uiState } from '../../../store/uiState';
import { drawerDraggerStyle } from '../../atoms/drawerDraggerStyle';
import { drawerCloseStyle } from '../../atoms/drawerCloseStyle';
import { drawerWrapperStyle } from '../../atoms/drawerWrapperStyle';
import { VerticalLeftOutlined } from '@ant-design/icons';
import {
  FORM_SIDEBAR_CONFIG,
  FORM_SIDEBAR_FIELDS,
} from '#types/System.Parameters.types';
import { UIElementType } from '#types/System.types';
import type { FormSidebarElementDefinition } from './index';

const FormTimeline = lazy(() => import('../form/FormTimeline/FormTimeline'));
const FormSidebarEntityContent = lazy(
  () => import('./FormSidebarEntityContent'),
);
const FormSidebarRemindersContent = lazy(() =>
  import('../FormReminders/').then((module) => ({
    default: module.FormSidebarRemindersContent,
  })),
);
const FormSidebarFormElementsContent = lazy(
  () => import('./FormSidebarFormElementsContent'),
);

/** Props for FormSidebar component */
interface FormSidebarProps {
  /** Whether the sidebar drawer is open */
  isOpen: boolean;
  /** Callback when sidebar is closed. Only invoked outside the entity view — there the Drawer closes via `sidebarControl.closeSidebar()` so the selection-change binding fires. */
  onClose: () => void;
  /** Currently active sidebar view */
  activeView: SidebarView;
  /** Callback to change the active view */
  setActiveView: (view: SidebarView) => void;
  /** When provided, only these views are shown. Hides the Segmented control when a single view. */
  availableViews?: SidebarView[];
}

const FormSidebar = ({
  isOpen,
  onClose,
  activeView,
  setActiveView,
  availableViews,
}: FormSidebarProps) => {
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const sidebarControl = useContext(SidebarControlContext);
  const { width } = useSnapshot(uiState.formSidebar, { sync: true });

  // Create "Log" section elements based on config
  const logElements = useMemo(() => {
    const elements: FormSidebarElementDefinition[] = [];

    if (FORM_SIDEBAR_CONFIG.enableCommunicationLog) {
      elements.push({
        id: 'sidebar-communicationLog',
        uiElement: {
          uiElementType: UIElementType.Field,
          fieldId: 'communicationLog',
          props: { logdisplay: 'compact' },
        },
      });
    }

    if (FORM_SIDEBAR_CONFIG.enableAttachments) {
      elements.push({
        id: 'sidebar-attachments',
        uiElement: {
          uiElementType: UIElementType.Attachments,
        },
      });
    }

    return elements;
  }, []);

  // Create "Fields" section elements from FORM_SIDEBAR_FIELDS array
  const fieldsElements = useMemo(() => {
    const elements: FormSidebarElementDefinition[] = [];
    const fieldIdentifiers = Array.isArray(FORM_SIDEBAR_FIELDS)
      ? FORM_SIDEBAR_FIELDS
      : [];

    fieldIdentifiers.forEach((identifier: string) => {
      elements.push({
        id: `sidebar-${identifier}`,
        uiElement: {
          uiElementType: UIElementType.Field,
          fieldId: identifier,
        },
      });
    });

    return elements;
  }, []);

  // Check if we have elements to show
  const hasLogElements = logElements.length > 0;
  const hasFieldsElements = fieldsElements.length > 0;

  // Build segmented control options, filtered by availableViews when provided
  const segmentedOptions = useMemo(() => {
    const allOptions = [
      { label: 'Record', value: 'entity' },
      { label: 'Timeline', value: 'timeline' },
      { label: 'Reminders', value: 'reminders' },
    ];

    if (hasLogElements) {
      allOptions.push({ label: 'Log', value: 'log' });
    }

    if (hasFieldsElements) {
      allOptions.push({ label: 'Fields', value: 'fields' });
    }

    if (availableViews) {
      return allOptions.filter((opt) =>
        availableViews.includes(opt.value as SidebarView),
      );
    }

    return allOptions;
  }, [hasLogElements, hasFieldsElements, availableViews]);

  // Validate active view and reset if needed
  useEffect(() => {
    const validValues = segmentedOptions.map((opt) => opt.value);
    if (validValues.length === 0) {
      console.warn('[FormSidebar] No valid views available');
      return;
    }
    if (!validValues.includes(activeView)) {
      setActiveView(validValues[0] as SidebarView);
    }
  }, [activeView, segmentedOptions, setActiveView]);

  // Determine content to render based on active view
  const renderContent = () => {
    if (activeView === 'timeline') return <FormTimeline />;
    if (activeView === 'reminders') return <FormSidebarRemindersContent />;

    if (activeView === 'log' && hasLogElements) {
      return <FormSidebarFormElementsContent elements={logElements} />;
    }

    if (activeView === 'fields' && hasFieldsElements) {
      return <FormSidebarFormElementsContent elements={fieldsElements} />;
    }

    // Default to entity view
    return <FormSidebarEntityContent />;
  };

  return (
    <Drawer
      title={
        segmentedOptions.length > 1 ? (
          <Segmented
            value={activeView}
            onChange={(value) => setActiveView(value as SidebarView)}
            options={segmentedOptions}
            block
            size={md ? undefined : 'small'}
            styles={md ? undefined : { item: { flex: '1 1 auto' } }}
          />
        ) : (
          segmentedOptions[0]?.label
        )
      }
      closeIcon={<VerticalLeftOutlined />}
      placement="right"
      mask={false}
      open={isOpen}
      destroyOnHidden
      onClose={() => {
        if (sidebarControl && activeView === 'entity') {
          sidebarControl.closeSidebar();
        } else {
          sidebarControl?.setNewRecordId(undefined);
          onClose();
        }
      }}
      size={width}
      maxSize={panelMaxWidth('formSidebar')}
      resizable={{ onResize: (next) => setPanelWidth('formSidebar', next) }}
      styles={{
        header: {
          padding: token.padding,
        },
        body: {
          padding: token.padding,
        },
        dragger: drawerDraggerStyle(token),
        wrapper: drawerWrapperStyle(token, 'formSidebar'),
        close: drawerCloseStyle(token),
      }}
    >
      <Suspense fallback={<Skeleton active />}>{renderContent()}</Suspense>
    </Drawer>
  );
};

FormSidebar.displayName = 'FormSidebar';

export default FormSidebar;
